import { useId } from 'react'
import { cn } from '../../lib/cn'

type CrestTone = 'pine' | 'gilt'

interface CrestProps {
  /**
   * `pine` — dark lettering inside brass rings, for cream grounds.
   * `gilt` — gilt rings and cream lettering, for pine/dark grounds.
   */
  tone?: CrestTone
  /** Renders the engraved "GTG" monogram at the centre (the medallion form). */
  monogram?: boolean
  className?: string
}

/**
 * The club seal — brass rings, GOT TO GO GOLF CLUB arced over · EST. 2026 ·,
 * and the pin-and-flag. Sized entirely by the caller (the SVG fills its box);
 * the design uses it at 38px in the header brandlock, 128px on the interior
 * mastheads, and as the homepage's hero medallion — 422px at desktop width,
 * capped at 320px once the hero stacks.
 *
 * The arcs the lettering rides on are referenced by id, so every instance mints
 * its own via useId() — two crests on one page with a shared `#arcTop` would
 * both resolve to whichever mounted first, and the second's lettering would
 * silently vanish.
 */
export function Crest({ tone = 'pine', monogram = false, className }: CrestProps) {
  const uid = useId()
  const arcTop = `crest-arc-top-${uid}`
  const arcBottom = `crest-arc-bottom-${uid}`

  const ring = tone === 'gilt' ? 'var(--color-gilt)' : 'var(--color-brass)'
  const clubName = tone === 'gilt' ? 'var(--color-paper)' : 'var(--color-pine)'
  const estd = tone === 'gilt' ? 'var(--color-gilt)' : 'var(--color-brass)'
  const pole = tone === 'gilt' ? 'var(--color-paper)' : 'var(--color-pine)'

  return (
    <svg
      viewBox="0 0 132 132"
      className={cn('block', className)}
      role="img"
      aria-label="Got To Go Golf Club crest"
    >
      <circle cx="66" cy="66" r="63" fill="none" stroke={ring} strokeWidth="1.5" />
      <circle cx="66" cy="66" r="54" fill="none" stroke={ring} strokeWidth="1" opacity="0.5" />

      {/* Baselines for the arced lettering — never painted.
          The bottom arc runs LEFT to right (sweep 0) so "· EST. 2026 ·" sits
          upright, the way a seal's lower legend reads. The design cards swept
          it right-to-left until 2026-08-29 — the legend rendered mirrored and
          upside down, verified against the cards' own markup — so this was a
          deliberate correction; every card source now draws the same arc. */}
      <path id={arcTop} d="M24 66a42 42 0 0 1 84 0" fill="none" />
      <path id={arcBottom} d="M24 66a42 42 0 0 0 84 0" fill="none" />

      <text fontSize="7.6" fontWeight="600" letterSpacing="1.7" fill={clubName} fontFamily="var(--font-text)">
        <textPath href={`#${arcTop}`} startOffset="50%" textAnchor="middle">
          GOT TO GO GOLF CLUB
        </textPath>
      </text>
      <text fontSize="7.5" fontWeight="600" letterSpacing="2.4" fill={estd} fontFamily="var(--font-text)">
        <textPath href={`#${arcBottom}`} startOffset="50%" textAnchor="middle">
          · EST. 2026 ·
        </textPath>
      </text>

      {/* The pin and flag. Flag stays pin-flag red in both tones — it is the
          one hot accent and reads on cream and pine alike. */}
      <path d="M60 90 L60 74 L80 79 L60 84" fill="var(--color-flag)" />
      <line x1="60" y1="90" x2="60" y2="72" stroke={pole} strokeWidth="1.6" />
      <circle cx="60" cy="71" r="2.4" fill={pole} />

      {monogram && (
        <text
          data-monogram="GTG"
          x="66"
          y="66"
          textAnchor="middle"
          dominantBaseline="central"
          fontFamily="var(--font-display)"
          fontWeight="600"
          fontSize="25"
          letterSpacing="0.8"
          fill={tone === 'gilt' ? 'var(--color-paper)' : 'var(--color-pine)'}
        >
          GTG
        </text>
      )}
    </svg>
  )
}
